//@ts-nocheck
//@ts-ignore
/* eslint-disable */
import './App.scss';
import Analytics from '@web-solutions/module-analytics';
import { useRemoteConfig } from 'core/hooks/use-remote-config';
import { Provider } from './providers/Provider';
import { useEffect, useLayoutEffect, useCallback, useRef, useState, useMemo } from 'react';
import { useStore } from './store/store';
import ReactPixel from 'react-facebook-pixel';
import DeviceProps from '@magnus/react-native-device-props';
import { useDispatch, useSelector } from 'react-redux';
import { load } from 'src/store/app/actions';
import { PaymentSystem } from '@web-solutions/react-billing';
import { selectPaymentSystem } from 'core/store/billing/selectors';
import { loadChargeblastTracker, setChargeblastEmailSource, setChargeblastEnabled } from 'core/utils/chargeblast';
import { components, layouts, backupFlow } from './consts';
import { debounce } from 'lodash';
import { getEarlyAchievableOffset } from './helpers/getEarlyAchievableOffset';
import { useNav } from './hooks/nav';
import { t } from '@web-solutions/module-localization';
import { Text } from './components/typography/Text/Text';
import { useProgressiveImageLoad } from './hooks/useProgressiveImageLoad';
import DynamicPage from './pages/dynamic-page';
import DynamicMultiPage from './pages/dynamic-page-multi';
import DynamicTransition from './pages/dynamic-transition';
import LtsLayout from './layouts/lts';
import TransitionLayoutNew from './layouts/transition';
import DynamicTransitionImage from './pages/dynamic-transition-with-image';
import DynamicTransitionImageTop from './pages/dynamic-transition-image-top';
import DynamicAnalyzingPage from './pages/dynamic-analyzing';
import DynamicPromisePage from './pages/dynamic-promise';
import DynamicProofPage from './pages/dynamic-proof';
import DynamicPersonalPlan from './pages/dynamic-personal-plan';
import { LTS_PAGES } from './constants/ltsPages';
import { generateLayout } from './layouts/layout-generator';

const parseDynamicPagesConfig = (dynamicPagesParam: any) => {
  try {
    if (!dynamicPagesParam) return [];

    if (Array.isArray(dynamicPagesParam)) {
      return dynamicPagesParam;
    }

    if (typeof dynamicPagesParam === 'string') {
      return JSON.parse(dynamicPagesParam);
    }

    return [];
  } catch (error) {
    return [];
  }
};

function App() {
  const { routeGroups: flow, fbPixelId, tiktokPixelId, dynamicPages } = useRemoteConfig();

  const currentPage = useStore((state) => state.currentPage);
  const setPage = useStore((state) => state.setPage);
  const when = useStore((state) => state.when);
  const skippedPages = useStore((state) => state.skippedPages);
  const addSkippedPage = useStore((state) => state.addSkippedPage);
  const removeSkippedPage = useStore((state) => state.removeSkippedPage);
  const paymentSystem = useSelector(selectPaymentSystem);
  const dispatch = useDispatch();

  const { onNext, onPrev } = useNav();
  const initializedRef = useRef(false);
  const [isRemoteConfigLoaded, setIsRemoteConfigLoaded] = useState(false);

  const dynamicPagesConfig = useMemo(() => {
    if (!dynamicPages) return [];

    const parsedConfig = parseDynamicPagesConfig(dynamicPages);

    return parsedConfig
      .filter((page) => page && (page.id || page['page-name']))
      .map((page, index) => ({
        id: page.id,
        title: page.title,
        subTitle: page.subTitle,
        options: Array.isArray(page.options)
          ? page.options.map((opt, optIndex) => ({
              emoji: opt.emoji || '',
              text: opt.text,
              value: opt.value,
            }))
          : [],
        choice: page.choice || 'single',
        stats: Array.isArray(page.stats)
          ? page.stats.map((opt, optIndex) => ({
              value: opt.value,
              label: opt.label,
            }))
          : [],
        feedbackTitle: page.feedbackTitle,
        feedbackBody: page.feedbackBody,
        buttonTitle: page.buttonTitle,
        sections: Array.isArray(page.sections) ? page.sections : [],
        type: page.type,
      }));
  }, [dynamicPages]);

  useEffect(() => {
    if (flow !== undefined) {
      setIsRemoteConfigLoaded(true);
    }
  }, [flow]);

  const getMergedFlow = useCallback(() => {
    if (!isRemoteConfigLoaded) {
      return backupFlow.flat();
    }

    const baseFlow = flow?.length > 0 ? flow : backupFlow;
    const flatFlow = baseFlow.flat();

    return flatFlow;
  }, [flow, isRemoteConfigLoaded, dynamicPagesConfig]);

  const quizFlow = useMemo(() => {
    return getMergedFlow();
  }, [getMergedFlow]);

  const fullPageFlow = useMemo(() => {
    return [...quizFlow, 'terms-of-use', 'privacy-policy'];
  }, [quizFlow]);

  useLayoutEffect(() => {
    if (!isRemoteConfigLoaded || currentPage) {
      return;
    }

    const firstPage = quizFlow[0];
    setPage(firstPage);
  }, [isRemoteConfigLoaded, flow, quizFlow, currentPage, setPage]);

  const init = async () => {
    await dispatch(load());
  };

  useLayoutEffect(() => {
    init();
  }, []);

  const pixelId = useMemo(() => {
    // Wait for Magnus: the store starts as {}, so fbPixelId is empty on mount.
    if (!isRemoteConfigLoaded) return undefined;

    return (fbPixelId && String(fbPixelId)) || process.env.REACT_APP_FB_APP_ID;
  }, [fbPixelId, isRemoteConfigLoaded]);

  useEffect(() => {
    if (!pixelId || initializedRef.current || typeof window === 'undefined') return;
    initializedRef.current = true;

    // Advanced matching: `external_id` is the Magnus IDFM, the same id SubTruck keys
    // its server-side (CAPI) events by. Passed at init so the browser pixel sends it
    // with every event and Web2App can read it back from the pixel's userData and
    // forward it to Magnus as `external_id` — without it both sides had none.
    DeviceProps.getIDFM().catch(() => undefined).then((idfm) => {
      const advancedMatching = idfm ? { external_id: idfm } : {};

      if (!window.fbq) {
        (function (f, b, e, v, n, t, s) {
          if (f.fbq) return;
          n = f.fbq = function () {
            n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
          };
          if (!f._fbq) f._fbq = n;
          n.push = n;
          n.loaded = true;
          n.version = '2.0';
          n.queue = [];
          t = b.createElement(e);
          t.async = true;
          t.src = v;
          s = b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t, s);
        })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
      }

      window.fbq('init', pixelId, advancedMatching);
      window.fbq('track', 'PageView');
      window.fbq.initialized = true;

      if (ReactPixel) {
        // Signature is (pixelId, advancedMatching, options): the options object used
        // to be passed in the advancedMatching slot.
        ReactPixel.init(pixelId, advancedMatching, { autoConfig: true, debug: true });
      }

      const noscript = document.createElement('noscript');
      noscript.innerHTML = `<img height="1" width="1" style="display:none" src="https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1"/>`;
      document.body.appendChild(noscript);
    });
  }, [pixelId]);

  // TikTok pixel: id приходит из remote config или .env (как fbPixelId), поднимаем только
  // для TikTok-трафика, иначе оплаты с Меты попадут в TikTok-пиксель.
  // Magnus (web2app) опрашивает window.ttq.instances() каждые 50 мс, поэтому
  // поздняя инициализация после загрузки конфига для серверных событий не страшна.
  useEffect(() => {
    if (!isRemoteConfigLoaded || typeof window === 'undefined') return;

    // Как fbPixelId: remote config в приоритете, иначе REACT_APP_TIKTOK_APP_ID из .env.
    const ttPixelId = (tiktokPixelId && String(tiktokPixelId).trim()) || process.env.REACT_APP_TIKTOK_APP_ID;
    if (!ttPixelId || window.ttq) return;

    const params = new URLSearchParams(window.location.search);
    const isTikTokTraffic = params.get('utm_source') === 'tiktok' || !!params.get('ttclid');
    if (!isTikTokTraffic) return;

    (function (w, d, t) {
      w.TiktokAnalyticsObject = t;
      var ttq = (w[t] = w[t] || []);
      ttq.methods = ['page', 'track', 'identify', 'instances', 'debug', 'on', 'off', 'once', 'ready', 'alias', 'group', 'enableCookie', 'disableCookie', 'holdConsent', 'revokeConsent', 'grantConsent'];
      ttq.setAndDefer = function (t, e) {
        t[e] = function () {
          t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
        };
      };
      for (var i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
      ttq.instance = function (t) {
        for (var e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++) ttq.setAndDefer(e, ttq.methods[n]);
        return e;
      };
      ttq.load = function (e, n) {
        var r = 'https://analytics.tiktok.com/i18n/pixel/events.js';
        ttq._i = ttq._i || {};
        ttq._i[e] = [];
        ttq._i[e]._u = r;
        ttq._t = ttq._t || {};
        ttq._t[e] = +new Date();
        ttq._o = ttq._o || {};
        ttq._o[e] = n || {};
        n = d.createElement('script');
        n.type = 'text/javascript';
        n.async = true;
        n.src = r + '?sdkid=' + e + '&lib=' + t;
        e = d.getElementsByTagName('script')[0];
        e.parentNode.insertBefore(n, e);
      };
      ttq.load(ttPixelId);
      ttq.page();
    })(window, document, 'ttq');
  }, [tiktokPixelId, isRemoteConfigLoaded]);

  useEffect(() => {
    if (!currentPage || !quizFlow.length || !isRemoteConfigLoaded) return;

    if (currentPage === quizFlow[0]) {
      Analytics.trackEvent('first_screen', 'loaded', {
        time: Math.round(performance.now() / 1000),
      });
    }

    const stepIndex = quizFlow.findIndex((page) => page === currentPage) + 1;
    if (stepIndex > 0) {
      Analytics.trackEvent(`step${stepIndex}`, 'view', {
        page: currentPage,
      });
    }

    if (currentPage === quizFlow[1]) {
      if (typeof ReactPixel !== 'undefined') {
        ReactPixel.trackCustom('step1_completed', { nexPage: currentPage });
      } else if (window.fbq) {
        fbq('trackCustom', 'step1_completed', { nexPage: currentPage });
      }
    }
  }, [currentPage, quizFlow, isRemoteConfigLoaded]);

  // Chargeblast (IP + email for chargeback deflection) runs with Stripe only. A purchase
  // without an email in hand (upsale after a reload) takes it from this store, read lazily.
  useEffect(() => {
    if (!isRemoteConfigLoaded) return;
    setChargeblastEmailSource(() => useStore.getState().email);
    setChargeblastEnabled(paymentSystem === PaymentSystem.STRIPE);
  }, [isRemoteConfigLoaded, paymentSystem]);

  // Chargeblast tracker (~1 KB): injected after the first screen, not from index.html,
  // so identify() fires without waiting at payment. A no-op unless enabled above.
  const isFirstScreenShown = isRemoteConfigLoaded && !!currentPage;
  useEffect(() => {
    if (isFirstScreenShown) loadChargeblastTracker();
  }, [isFirstScreenShown, paymentSystem]);

  useEffect(() => {
    if (currentPage !== 'goal-2') return;

    const offset = getEarlyAchievableOffset(when);
    const goal2Skipped = skippedPages.includes('goal-2');

    if (offset === null && !goal2Skipped) {
      addSkippedPage('goal-2');
      onNext();
    }
  }, [when, currentPage, skippedPages, onNext]);

  useEffect(() => {
    const offset = getEarlyAchievableOffset(when);
    const shouldShowGoal2 = offset !== null;
    const goal2Skipped = skippedPages.includes('goal-2');

    if (shouldShowGoal2 && goal2Skipped) {
      removeSkippedPage('goal-2');
    }
  }, [when, skippedPages]);

  const debouncedTrackEvent = useCallback(
    debounce((flow) => {
      Analytics.trackEvent(`quizFlow`, 'empty', { flow });
    }, 2000),
    []
  );

  useEffect(() => {
    if (flow?.length === 0 || flow === undefined) {
      debouncedTrackEvent(flow);
    }

    return () => {
      debouncedTrackEvent.cancel();
    };
  }, [flow, debouncedTrackEvent]);

  window.scrollTo(0, 0);

  useProgressiveImageLoad(currentPage, quizFlow);

  if (!isRemoteConfigLoaded) {
    return (
      <div className='page-loader'>
        <svg
          xmlns='http://www.w3.org/2000/svg'
          width='74px'
          height='74px'
          viewBox='0 0 100 100'
          preserveAspectRatio='xMidYMid'
        >
          {/* magenta → violet, the funnel's own gradient (--hs-grad) */}
          <defs>
            <linearGradient id='hairstyle-loader' x1='0' y1='0' x2='1' y2='1'>
              <stop offset='0%' stopColor='#ff3d81' />
              <stop offset='100%' stopColor='#9b2cff' />
            </linearGradient>
          </defs>
          <circle
            cx='50'
            cy='50'
            fill='none'
            stroke='url(#hairstyle-loader)'
            strokeWidth='10'
            r='35'
            strokeLinecap='round'
            strokeDasharray='164.93361431346415 56.97787143782138'
          ></circle>
        </svg>

        <Text semibold>{t('onboarding.preLoader.title')}</Text>
        <Text className='description' size='m' medium>
          {t('onboarding.preLoader.subTitle')}
        </Text>
      </div>
    );
  }

  const layout = generateLayout(fullPageFlow, dynamicPagesConfig, onNext, onPrev);
  const currentLayout = layout[currentPage];

  return (
    <div className='App'>
      <Provider>{currentLayout}</Provider>
    </div>
  );
}

export default App;
